import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  LinearProgress,
  alpha,
  useTheme,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemText,
  Divider,
  Checkbox,
  Slider,
  Popover
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ContactPhone as ContactPhoneIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  ViewColumn as ViewColumnIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../../contexts/AccountContext';
import { useLeadReport } from '../../../contexts/LeadReportContext';
import teamService from '../../../services/teamService';
import { getRandomAvatarColor } from '../../../utils/helper';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Helper function to parse Indian currency format (₹45L, ₹1.5Cr) to numeric value
const parseIndianCurrency = (valueStr) => {
  if (!valueStr || typeof valueStr !== 'string') return 0;
  
  // Remove ₹ symbol and whitespace
  const cleanStr = valueStr.replace(/₹/g, '').trim();
  
  // Check for Crore (Cr)
  if (cleanStr.toUpperCase().includes('CR')) {
    const numStr = cleanStr.replace(/CR/gi, '').trim();
    const numValue = parseFloat(numStr) || 0;
    return Math.round(numValue * 10000000); // Convert to rupees
  }
  
  // Check for Lakh (L)
  if (cleanStr.toUpperCase().includes('L')) {
    const numStr = cleanStr.replace(/L/gi, '').trim();
    const numValue = parseFloat(numStr) || 0;
    return Math.round(numValue * 100000); // Convert to rupees
  }
  
  // Try to parse as regular number
  return parseFloat(cleanStr) || 0;
};

const LeadTable = ({ dateRangeState, selectedTeams = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { accountId } = useAccount();
  const { fetchLeadSmartView, fetchSmartViewFilters } = useLeadReport();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInterestedProperties, setSelectedInterestedProperties] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedChannelPartner, setSelectedChannelPartner] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedTags, setSelectedTags] = useState('all');
  const [selectedProjectType, setSelectedProjectType] = useState('all');
  const [budgetRange, setBudgetRange] = useState([0, 100000000]); // [min, max] in rupees (0 to 10Cr) - actual filter value
  const [tempBudgetRange, setTempBudgetRange] = useState([0, 100000000]); // Temporary value for slider while dragging
  const [budgetPopoverAnchor, setBudgetPopoverAnchor] = useState(null);
  const [visitCountFilter, setVisitCountFilter] = useState('any');
  const [lastContactedFilter, setLastContactedFilter] = useState('all');
  const [customLastContactedStart, setCustomLastContactedStart] = useState(dayjs());
  const [customLastContactedEnd, setCustomLastContactedEnd] = useState(dayjs());
  const [customLastContactedDialogOpen, setCustomLastContactedDialogOpen] = useState(false);
  const [selectedFunnelMetric, setSelectedFunnelMetric] = useState(null); // Track selected funnel metric
  const [funnelMetricFilter, setFunnelMetricFilter] = useState(null); // Track special filter for funnel metric
  const [funnelMetricStatusFilter, setFunnelMetricStatusFilter] = useState(null); // Track status filter for funnel metric (e.g., closed_won)
  
  // Column selection state
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);
  
  // Default columns definition (fallback)
  const defaultColumns = [
    { id: 'leadDetails', label: 'Lead Details', defaultVisible: true, alwaysVisible: true },
    { id: 'source', label: 'Source', defaultVisible: true, alwaysVisible: false },
    { id: 'agent', label: 'Agent', defaultVisible: true, alwaysVisible: false },
    { id: 'team', label: 'Team', defaultVisible: false, alwaysVisible: false },
    { id: 'stage', label: 'Stage', defaultVisible: true, alwaysVisible: false },
    { id: 'status', label: 'Status', defaultVisible: false, alwaysVisible: false },
    { id: 'value', label: 'Value', defaultVisible: true, alwaysVisible: false },
    { id: 'priority', label: 'Priority', defaultVisible: false, alwaysVisible: false },
    { id: 'lastContacted', label: 'Last Contacted', defaultVisible: true, alwaysVisible: false },
    { id: 'visitCount', label: 'Visits Count', defaultVisible: true, alwaysVisible: false },
    { id: 'project', label: 'Project', defaultVisible: true, alwaysVisible: false },
    { id: 'channelPartner', label: 'Channel Partner', defaultVisible: false, alwaysVisible: false },
    { id: 'createdAt', label: 'Created At', defaultVisible: false, alwaysVisible: false },
    { id: 'tags', label: 'Tags/Preferences', defaultVisible: false, alwaysVisible: false },
    { id: 'probability', label: 'Probability', defaultVisible: false, alwaysVisible: false },
  ];
  
  // Initialize visible columns from localStorage or defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('leadTableVisibleColumns');
    if (saved) {
      try {
        const columns = JSON.parse(saved);
        // Ensure Lead Details is always included
        if (!columns.includes('leadDetails')) {
          columns.unshift('leadDetails');
        }
        return columns;
      } catch (e) {
        // Fallback to default columns
        const columns = defaultColumns.filter(col => col.defaultVisible).map(col => col.id);
        if (!columns.includes('leadDetails')) {
          columns.unshift('leadDetails');
        }
        return columns;
      }
    } else {
      // Use default columns initially
      const columns = defaultColumns.filter(col => col.defaultVisible).map(col => col.id);
      if (!columns.includes('leadDetails')) {
        columns.unshift('leadDetails');
      }
      return columns;
    }
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [leadTableData, setLeadTableData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [funnelMetricsData, setFunnelMetricsData] = useState(null);
  const [loading, setLoading] = useState(false); // Start with false, will be set to true when fetching
  const [exporting, setExporting] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({
    lead_stage: [],
    source: [],
    tags: [],
    intereseted_properties: [], // Note: typo in API response
    bhk_type: [],
    channel_partner: [],
    agents: [],
    teams: [],
    columns: [],
  });
  const [loadingFilters, setLoadingFilters] = useState(true);

  // Get all available columns from backend or use defaults
  const allColumns = useMemo(() => {
    if (filterOptions.columns && filterOptions.columns.length > 0) {
      return filterOptions.columns;
    }
    return defaultColumns;
  }, [filterOptions.columns]);


  // Fetch lead table data
  useEffect(() => {
    const fetchLeadData = async () => {
      if (!dateRangeState) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      const { currentStart, currentEnd } = dateRangeState;
      const startDate = currentStart.format('YYYY-MM-DD');
      const endDate = currentEnd.format('YYYY-MM-DD');

      try {
        // Build filters object
        const filters = {};
        
        // Search filter
        if (searchTerm && searchTerm.trim()) {
          filters.search = searchTerm.trim();
        }
        
        // Stage filter - send as list (single value wrapped in array)
        // Only apply stage filter if not using a special funnel metric filter
        if (selectedStage !== 'all' && !funnelMetricFilter) {
          const stageOption = filterOptions.lead_stage.find(s => s.value === selectedStage || s.label === selectedStage);
          const stageValue = stageOption ? stageOption.value : selectedStage;
          filters.stage_filter = [stageValue];  // Always send as array
        }
        
        // Source filter - send as list (single value wrapped in array)
        if (selectedSource !== 'all') {
          const sourceOption = filterOptions.source.find(s => s.value === selectedSource || s.label === selectedSource);
          const sourceValue = sourceOption ? sourceOption.value : selectedSource;
          filters.source_filter = [sourceValue];  // Always send as array
        }
        
        // Agent filter - expects agent_id (send as list)
        if (selectedAgent !== 'all') {
          filters.agent_filter = [selectedAgent];  // Always send as array
        }
        
        // Team filter - send team IDs as list (from top-level filter)
        if (selectedTeams && selectedTeams.length > 0) {
          filters.team_filter = selectedTeams;  // Already an array
        }
        
        // Channel partner filter - send as list (single value wrapped in array)
        if (selectedChannelPartner !== 'all') {
          const partnerOption = filterOptions.channel_partner.find(p => 
            p.value === selectedChannelPartner || p.label === selectedChannelPartner
          );
          const partnerValue = partnerOption ? partnerOption.value : selectedChannelPartner;
          filters.channel_partner_filter = [partnerValue];  // Always send as array
        }
        
        // Project filter (interested properties) - send array of IDs (multi-select)
        if (selectedInterestedProperties.length > 0) {
          // Extract IDs from selected properties
          const propertyIds = selectedInterestedProperties.map(prop => 
            typeof prop === 'object' ? prop.value : prop
          ).filter(Boolean);
          if (propertyIds.length > 0) {
            filters.project_filter = propertyIds;  // Send as array
          }
        }
        
        // Budget filter - use slider range values
        const [budgetMin, budgetMax] = budgetRange;
        // Only apply filter if it's not the default range (0 to 10Cr)
        if (budgetMin > 0 || budgetMax < 100000000) {
            filters.budget_filter = {};
          if (budgetMin > 0) {
            filters.budget_filter.min_budget = budgetMin;
            }
          if (budgetMax < 100000000) {
            filters.budget_filter.max_budget = budgetMax;
          }
        }
        
        // Tags filter - send as list (single value wrapped in array)
        if (selectedTags !== 'all') {
          const tagOption = filterOptions.tags.find(t => t.value === selectedTags || t.label === selectedTags);
          const tagValue = tagOption ? tagOption.value : selectedTags;
          filters.tags = [tagValue];  // Always send as array
        }
        
        // Project type (BHK type) filter - send as list (single value wrapped in array)
        if (selectedProjectType !== 'all') {
          const typeOption = filterOptions.bhk_type.find(t => t.value === selectedProjectType || t.label === selectedProjectType);
          const typeValue = typeOption ? typeOption.value : selectedProjectType;
          filters.bhk_type = [typeValue];  // Always send as array
        }

        // Last Contacted filter
        if (lastContactedFilter !== 'all') {
          const dateRange = getLastContactedDateRange(lastContactedFilter);
          if (dateRange) {
            filters.last_contacted_filter = {
              start_date: dateRange.startDate,
              end_date: dateRange.endDate
            };
          }
        }

        // Visit Count filter
        if (visitCountFilter !== 'any') {
          filters.visit_count_filter = {};
          if (visitCountFilter === '1') {
            filters.visit_count_filter.min_count = 1;
            filters.visit_count_filter.max_count = 1;
          } else if (visitCountFilter === '2') {
            filters.visit_count_filter.min_count = 2;
            filters.visit_count_filter.max_count = 2;
          } else if (visitCountFilter === '3') {
            filters.visit_count_filter.min_count = 3;
            filters.visit_count_filter.max_count = 3;
          } else if (visitCountFilter === 'more_than_3') {
            filters.visit_count_filter.min_count = 4;
          }
        }

        // Status filter (for bookings_closed: stage='closure' AND status='closed_won')
        if (funnelMetricStatusFilter) {
          filters.status_filter = [funnelMetricStatusFilter];  // Send as array
        }

        // Funnel metric special filters (use the same logic as counting)
        // These filters use the exact same logic as the metric calculation
        if (funnelMetricFilter) {
          if (funnelMetricFilter === 'contacted_filter') {
            filters.contacted_filter = true;
          } else if (funnelMetricFilter === 'details_shared_filter') {
            filters.details_shared_filter = true;
          } else if (funnelMetricFilter === 'visit_scheduled_filter') {
            filters.visit_scheduled_filter = true;
          } else if (funnelMetricFilter === 'visit_completed_filter') {
            filters.visit_completed_filter = true;
          }
        }

        // Add sorting parameters
        if (sortField) {
          filters.sort_by = sortField;
          filters.sort_order = sortOrder;
        }

        const response = await fetchLeadSmartView(
          startDate,
          endDate,
          page + 1, // API uses 1-based pagination
          rowsPerPage,
          filters
        );

        if (response) {
          setLeadTableData(response.leads || []);
          setPagination(response.pagination || null);
          setFunnelMetricsData(response.funnel_metrics || null);
        } else {
          setLeadTableData([]);
          setPagination(null);
          setFunnelMetricsData(null);
        }
      } catch (error) {
        console.error('Error fetching lead table data:', error);
        setLeadTableData([]);
        setPagination(null);
        setFunnelMetricsData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLeadData();
  }, [dateRangeState, page, rowsPerPage, searchTerm, selectedStage, selectedSource, selectedAgent, selectedTeams, selectedChannelPartner, selectedInterestedProperties, budgetRange, visitCountFilter, selectedTags, selectedProjectType, lastContactedFilter, customLastContactedStart, customLastContactedEnd, selectedFunnelMetric, funnelMetricFilter, funnelMetricStatusFilter, fetchLeadSmartView, sortField, sortOrder]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch smart view filters
  useEffect(() => {
    const fetchSmartViewFiltersData = async () => {
      try {
        setLoadingFilters(true);
        const response = await fetchSmartViewFilters();
        if (response) {
          const newFilterOptions = {
            lead_stage: response.lead_stage || [],
            source: response.source || [],
            tags: response.tags || [],
            intereseted_properties: response.intereseted_properties || [],
            bhk_type: response.bhk_type || [],
            channel_partner: response.channel_partner || [],
            agents: response.agents || [],
            teams: response.teams || [],
            columns: response.columns || [],
          };
          setFilterOptions(newFilterOptions);
          
          // Update visible columns if columns data is available and localStorage doesn't have saved data
          if (response.columns && response.columns.length > 0) {
            const saved = localStorage.getItem('leadTableVisibleColumns');
            if (!saved) {
              // Initialize from backend defaults
              const defaultVisibleColumns = response.columns
                .filter(col => col.defaultVisible)
                .map(col => col.id);
              // Ensure Lead Details is always included
              if (!defaultVisibleColumns.includes('leadDetails')) {
                defaultVisibleColumns.unshift('leadDetails');
              }
              setVisibleColumns(defaultVisibleColumns);
              localStorage.setItem('leadTableVisibleColumns', JSON.stringify(defaultVisibleColumns));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching smart view filters:', error);
      } finally {
        setLoadingFilters(false);
      }
    };
    fetchSmartViewFiltersData();
  }, [fetchSmartViewFilters]);

  // Helper function to calculate last contacted date range
  const getLastContactedDateRange = (filterOption) => {
    const today = dayjs();
    switch (filterOption) {
      case 'today':
        return {
          startDate: today.startOf('day').format('YYYY-MM-DD'),
          endDate: today.endOf('day').format('YYYY-MM-DD')
        };
      case 'yesterday':
        const yesterday = today.subtract(1, 'day');
        return {
          startDate: yesterday.startOf('day').format('YYYY-MM-DD'),
          endDate: yesterday.endOf('day').format('YYYY-MM-DD')
        };
      case 'this_month':
        return {
          startDate: today.startOf('month').format('YYYY-MM-DD'),
          endDate: today.endOf('day').format('YYYY-MM-DD')
        };
      case 'this_quarter':
        // Calculate quarter manually: Q1 (0-2), Q2 (3-5), Q3 (6-8), Q4 (9-11)
        const currentQuarter = Math.floor(today.month() / 3);
        const quarterStartMonth = currentQuarter * 3;
        const quarterStart = today.month(quarterStartMonth).startOf('month');
        return {
          startDate: quarterStart.format('YYYY-MM-DD'),
          endDate: today.endOf('day').format('YYYY-MM-DD')
        };
      case 'custom':
        return {
          startDate: customLastContactedStart.format('YYYY-MM-DD'),
          endDate: customLastContactedEnd.format('YYYY-MM-DD')
        };
      default:
        return null;
    }
  };

  // Handle last contacted filter change
  const handleLastContactedFilterChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setLastContactedFilter('custom');
      setCustomLastContactedDialogOpen(true);
    } else {
      setLastContactedFilter(value);
      setPage(0); // Reset to first page when filter changes
    }
  };

  // Handle custom date dialog close
  const handleCustomLastContactedDialogClose = () => {
    // If dialog is closed without applying, revert to 'all'
    setLastContactedFilter('all');
    setCustomLastContactedDialogOpen(false);
  };

  // Handle custom date apply
  const handleCustomLastContactedApply = () => {
    if (customLastContactedStart && customLastContactedEnd && !customLastContactedStart.isAfter(customLastContactedEnd)) {
      setCustomLastContactedDialogOpen(false);
      setPage(0); // Reset to first page
      // Filter is already set to 'custom', so no need to change it
    }
  };

  // Get date range display for Last Contacted filter
  const getLastContactedDateDisplay = () => {
    if (lastContactedFilter === 'all') return '';
    const dateRange = getLastContactedDateRange(lastContactedFilter);
    if (!dateRange) return '';
    
    const start = dayjs(dateRange.startDate);
    const end = dayjs(dateRange.endDate);
    
    // If same day, show single date
    if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
      return start.format('MMM DD, YYYY');
    }
    
    // Show date range
    return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
  };

  // Column selection handlers
  const handleColumnMenuOpen = (event) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleToggleColumn = (columnId) => {
    // Check if column is always visible (from backend)
    const column = allColumns.find(col => col.id === columnId);
    if (column && column.alwaysVisible) return;
    
    const newVisibleColumns = visibleColumns.includes(columnId)
      ? visibleColumns.filter(id => id !== columnId)
      : [...visibleColumns, columnId];
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('leadTableVisibleColumns', JSON.stringify(newVisibleColumns));
  };

  // Format currency for slider labels
  const formatBudgetCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(value % 10000000 === 0 ? 0 : 1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(value % 100000 === 0 ? 0 : 1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(0)}K`;
    }
    return `₹${value}`;
  };

  // Handle temporary budget range change (while dragging slider)
  const handleTempBudgetRangeChange = (event, newValue) => {
    setTempBudgetRange(newValue);
  };

  // Handle budget popover open - initialize temp state from actual state
  const handleBudgetClick = (event) => {
    setTempBudgetRange([...budgetRange]); // Copy current budget range to temp
    setBudgetPopoverAnchor(event.currentTarget);
  };

  // Handle budget popover close without applying
  const handleBudgetPopoverClose = () => {
    setBudgetPopoverAnchor(null);
    // Reset temp to actual value when closing without applying
    setTempBudgetRange([...budgetRange]);
  };

  // Handle apply button click - update actual filter and close popover
  const handleBudgetApply = () => {
    setBudgetRange([...tempBudgetRange]);
    setPage(0); // Reset to first page when filter changes
    setBudgetPopoverAnchor(null);
  };

  const budgetPopoverOpen = Boolean(budgetPopoverAnchor);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate funnel metrics from backend data
  const totalLeads = funnelMetricsData?.total_leads || pagination?.total_count || 0;
  const leadContacted = funnelMetricsData?.lead_contacted?.value || 0;
  const detailsShared = funnelMetricsData?.details_shared?.value || 0;
  const visitScheduled = funnelMetricsData?.visit_scheduled?.value || 0;
  const visitCompleted = funnelMetricsData?.visit_completed?.value || 0;
  const bookingsClosed = funnelMetricsData?.bookings_closed?.value || 0;

  const funnelMetrics = [
    {
      id: 'total_leads',
      label: 'Total Leads',
      value: totalLeads,
      percentage: 100,
      color: '#3b82f6',
      icon: <PersonIcon fontSize="small" />,
      stageFilter: null, // No filter for all leads
      specialFilter: null
    },
    {
      id: 'lead_contacted',
      label: 'Lead Contacted',
      value: leadContacted,
      percentage: funnelMetricsData?.lead_contacted?.percentage || (totalLeads > 0 ? Math.round((leadContacted / totalLeads) * 100) : 0),
      color: '#8b5cf6',
      icon: <ContactPhoneIcon fontSize="small" />,
      stageFilter: null, // Don't use stage filter, use special filter instead
      specialFilter: 'contacted_filter' // Use contacted_filter instead of stage
    },
    {
      id: 'details_shared',
      label: 'Details Shared',
      value: detailsShared,
      percentage: funnelMetricsData?.details_shared?.percentage || (totalLeads > 0 ? Math.round((detailsShared / totalLeads) * 100) : 0),
      color: '#ec4899',
      icon: <AssignmentIcon fontSize="small" />,
      stageFilter: null,
      specialFilter: 'details_shared_filter' // Use details_shared_filter instead of stage
    },
    {
      id: 'visit_scheduled',
      label: 'Visit Scheduled',
      value: visitScheduled,
      percentage: funnelMetricsData?.visit_scheduled?.percentage || (totalLeads > 0 ? Math.round((visitScheduled / totalLeads) * 100) : 0),
      color: '#f59e0b',
      icon: <ScheduleIcon fontSize="small" />,
      stageFilter: null,
      specialFilter: 'visit_scheduled_filter' // Use visit_scheduled_filter instead of stage
    },
    {
      id: 'visit_completed',
      label: 'Visit Completed',
      value: visitCompleted,
      percentage: funnelMetricsData?.visit_completed?.percentage || (totalLeads > 0 ? Math.round((visitCompleted / totalLeads) * 100) : 0),
      color: '#10b981',
      icon: <CheckCircleIcon fontSize="small" />,
      stageFilter: null,
      specialFilter: 'visit_completed_filter' // Use visit_completed_filter instead of stage
    },
    {
      id: 'bookings_closed',
      label: 'Bookings Closed',
      value: bookingsClosed,
      percentage: funnelMetricsData?.bookings_closed?.percentage || (totalLeads > 0 ? Math.round((bookingsClosed / totalLeads) * 100) : 0),
      color: '#06b6d4',
      icon: <MoneyIcon fontSize="small" />,
      stageFilter: 'closure', // Use stage filter for bookings closed
      statusFilter: 'closed_won', // Also need status filter for bookings closed
      specialFilter: null
    }
  ];

  // Build filters object for export (reusable function)
  const buildFiltersForExport = () => {
    const filters = {};
    
    // Search filter
    if (searchTerm && searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }
    
    // Stage filter
    if (selectedStage !== 'all' && !funnelMetricFilter) {
      const stageOption = filterOptions.lead_stage.find(s => s.value === selectedStage || s.label === selectedStage);
      const stageValue = stageOption ? stageOption.value : selectedStage;
      filters.stage_filter = [stageValue];
    }
    
    // Source filter
    if (selectedSource !== 'all') {
      const sourceOption = filterOptions.source.find(s => s.value === selectedSource || s.label === selectedSource);
      const sourceValue = sourceOption ? sourceOption.value : selectedSource;
      filters.source_filter = [sourceValue];
    }
    
    // Agent filter
    if (selectedAgent !== 'all') {
      filters.agent_filter = [selectedAgent];
    }
    
    // Team filter (from top-level filter)
    if (selectedTeams && selectedTeams.length > 0) {
      filters.team_filter = selectedTeams;
    }
    
    // Channel partner filter
    if (selectedChannelPartner !== 'all') {
      const partnerOption = filterOptions.channel_partner.find(p => 
        p.value === selectedChannelPartner || p.label === selectedChannelPartner
      );
      const partnerValue = partnerOption ? partnerOption.value : selectedChannelPartner;
      filters.channel_partner_filter = [partnerValue];
    }
    
    // Project filter
    if (selectedInterestedProperties.length > 0) {
      const propertyIds = selectedInterestedProperties.map(prop => 
        typeof prop === 'object' ? prop.value : prop
      ).filter(Boolean);
      if (propertyIds.length > 0) {
        filters.project_filter = propertyIds;
      }
    }
    
    // Budget filter
    const [budgetMin, budgetMax] = budgetRange;
    if (budgetMin > 0 || budgetMax < 100000000) {
      filters.budget_filter = {};
      if (budgetMin > 0) {
        filters.budget_filter.min_budget = budgetMin;
      }
      if (budgetMax < 100000000) {
        filters.budget_filter.max_budget = budgetMax;
      }
    }
    
    // Tags filter
    if (selectedTags !== 'all') {
      const tagOption = filterOptions.tags.find(t => t.value === selectedTags || t.label === selectedTags);
      const tagValue = tagOption ? tagOption.value : selectedTags;
      filters.tags = [tagValue];
    }
    
    // Project type filter
    if (selectedProjectType !== 'all') {
      const typeOption = filterOptions.bhk_type.find(t => t.value === selectedProjectType || t.label === selectedProjectType);
      const typeValue = typeOption ? typeOption.value : selectedProjectType;
      filters.bhk_type = [typeValue];
    }
    
    // Last Contacted filter
    if (lastContactedFilter !== 'all') {
      const dateRange = getLastContactedDateRange(lastContactedFilter);
      if (dateRange) {
        filters.last_contacted_filter = {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        };
      }
    }
    
    // Visit Count filter
    if (visitCountFilter !== 'any') {
      filters.visit_count_filter = {};
      if (visitCountFilter === '1') {
        filters.visit_count_filter.min_count = 1;
        filters.visit_count_filter.max_count = 1;
      } else if (visitCountFilter === '2') {
        filters.visit_count_filter.min_count = 2;
        filters.visit_count_filter.max_count = 2;
      } else if (visitCountFilter === '3') {
        filters.visit_count_filter.min_count = 3;
        filters.visit_count_filter.max_count = 3;
      } else if (visitCountFilter === 'more_than_3') {
        filters.visit_count_filter.min_count = 4;
      }
    }
    
    // Status filter
    if (funnelMetricStatusFilter) {
      filters.status_filter = [funnelMetricStatusFilter];
    }
    
    // Funnel metric special filters
    if (funnelMetricFilter) {
      if (funnelMetricFilter === 'contacted_filter') {
        filters.contacted_filter = true;
      } else if (funnelMetricFilter === 'details_shared_filter') {
        filters.details_shared_filter = true;
      } else if (funnelMetricFilter === 'visit_scheduled_filter') {
        filters.visit_scheduled_filter = true;
      } else if (funnelMetricFilter === 'visit_completed_filter') {
        filters.visit_completed_filter = true;
      }
    }
    
    // Add sorting parameters
    if (sortField) {
      filters.sort_by = sortField;
      filters.sort_order = sortOrder;
    }
    
    return filters;
  };

  // Fetch all leads for export (no pagination)
  const fetchAllLeadsForExport = async () => {
    if (!dateRangeState) {
      return [];
    }
    
    const { currentStart, currentEnd } = dateRangeState;
    const startDate = currentStart.format('YYYY-MM-DD');
    const endDate = currentEnd.format('YYYY-MM-DD');
    const filters = buildFiltersForExport();
    
    // Fetch with large page size to get all results
    const response = await fetchLeadSmartView(
      startDate,
      endDate,
      1,
      10000, // Large page size to get all leads
      filters
    );
    
    return response?.leads || [];
  };

  // Convert leads to CSV format
  const convertLeadsToCSV = (leads) => {
    if (!Array.isArray(leads) || leads.length === 0) {
      return '';
    }

    // Get column headers based on visible columns
    const headers = [];
    visibleColumns.forEach(colId => {
      if (colId === 'leadDetails') {
        headers.push('Lead ID', 'Name', 'Phone', 'Email');
      } else {
        const col = allColumns.find(c => c.id === colId);
        if (col) {
          headers.push(col.label);
        }
      }
    });

    // Convert leads to CSV rows
    const csvRows = leads.map(lead => {
      const row = [];
      
      visibleColumns.forEach(colId => {
        if (colId === 'leadDetails') {
          row.push(lead.lead_id || '');
          row.push(lead.customer?.name || '');
          row.push(lead.customer?.phone || '');
          row.push(lead.customer?.email || '');
          return;
        }
        
        let value = '';
        switch (colId) {
          case 'source':
            value = lead.source_verbose || lead.source || '';
            break;
          case 'agent':
            value = lead.agent?.name || '';
            break;
          case 'team':
            value = lead.team?.name || '';
            break;
          case 'stage':
            value = lead.stage_verbose || lead.stage || '';
            break;
          case 'status':
            value = lead.status_verbose || lead.status || '';
            break;
          case 'value':
            value = lead.value || '';
            break;
          case 'priority':
            value = lead.priority || '';
            break;
          case 'lastContacted':
            value = lead.last_contact_at || '';
            break;
          case 'visitCount':
            value = lead.visit_count || 0;
            break;
          case 'project':
            value = lead.project || '';
            break;
          case 'channelPartner':
            value = lead.channel_partner?.name || '';
            break;
          case 'createdAt':
            value = lead.created_at || '';
            break;
          case 'tags':
            value = Array.isArray(lead.preferences) ? lead.preferences.join(', ') : '';
            break;
          case 'probability':
            value = lead.probability || '';
            break;
          default:
            value = '';
        }
        row.push(value);
      });
      
      return row;
    });

    // Combine headers and rows with proper CSV escaping
    const csvContent = [headers, ...csvRows]
      .map(row => 
        row.map(field => {
          const escaped = String(field).replace(/"/g, '""');
          if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
            return `"${escaped}"`;
          }
          return escaped;
        }).join(',')
      )
      .join('\n');

    return csvContent;
  };

  // Export to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const leads = await fetchAllLeadsForExport();
      if (leads.length === 0) {
        alert('No leads to export');
        return;
      }
      
      // Warn if export is truncated
      const totalCount = pagination?.total_count || 0;
      if (leads.length >= 10000 && totalCount > 10000) {
        const confirmed = window.confirm(
          `Warning: Your export contains ${leads.length.toLocaleString()} leads out of ${totalCount.toLocaleString()} total. ` +
          `Only the first 10,000 leads will be exported. Do you want to continue?`
        );
        if (!confirmed) {
          setExporting(false);
          return;
        }
      }
      
      const csvData = convertLeadsToCSV(leads);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel (using CSV format with .xlsx extension)
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const leads = await fetchAllLeadsForExport();
      if (leads.length === 0) {
        alert('No leads to export');
        return;
      }
      
      // Warn if export is truncated
      const totalCount = pagination?.total_count || 0;
      if (leads.length >= 10000 && totalCount > 10000) {
        const confirmed = window.confirm(
          `Warning: Your export contains ${leads.length.toLocaleString()} leads out of ${totalCount.toLocaleString()} total. ` +
          `Only the first 10,000 leads will be exported. Do you want to continue?`
        );
        if (!confirmed) {
          setExporting(false);
          return;
        }
      }
      
      const csvData = convertLeadsToCSV(leads);
      // Excel can open CSV files, so we'll use CSV format with .xlsx extension
      const blob = new Blob(['\ufeff' + csvData], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Export to PDF
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const leads = await fetchAllLeadsForExport();
      if (leads.length === 0) {
        alert('No leads to export');
        return;
      }

      // Create a printable HTML table
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to export PDF');
        return;
      }

      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Leads Export</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { border-collapse: collapse; width: 100%; font-size: 12px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              tr:nth-child(even) { background-color: #f9f9f9; }
              @media print {
                body { margin: 0; }
                table { page-break-inside: auto; }
                tr { page-break-inside: avoid; page-break-after: auto; }
              }
            </style>
          </head>
          <body>
            <h2>Leads Export - ${new Date().toLocaleDateString()}</h2>
            <table>
              <thead>
                <tr>
                  ${visibleColumns.map(colId => {
                    if (colId === 'leadDetails') {
                      return '<th>Lead ID</th><th>Name</th><th>Phone</th><th>Email</th>';
                    }
                    const col = allColumns.find(c => c.id === colId);
                    return col ? `<th>${col.label}</th>` : '';
                  }).join('')}
                </tr>
              </thead>
              <tbody>
                ${leads.map(lead => {
                  const cells = [];
                  visibleColumns.forEach(colId => {
                    if (colId === 'leadDetails') {
                      cells.push(`<td>${lead.lead_id || ''}</td>`);
                      cells.push(`<td>${lead.customer?.name || ''}</td>`);
                      cells.push(`<td>${lead.customer?.phone || ''}</td>`);
                      cells.push(`<td>${lead.customer?.email || ''}</td>`);
                      return;
                    }
                    
                    let value = '';
                    switch (colId) {
                      case 'source':
                        value = lead.source_verbose || lead.source || '';
                        break;
                      case 'agent':
                        value = lead.agent?.name || '';
                        break;
                      case 'team':
                        value = lead.team?.name || '';
                        break;
                      case 'stage':
                        value = lead.stage_verbose || lead.stage || '';
                        break;
                      case 'status':
                        value = lead.status_verbose || lead.status || '';
                        break;
                      case 'value':
                        value = lead.value || '';
                        break;
                      case 'priority':
                        value = lead.priority || '';
                        break;
                      case 'lastContacted':
                        value = lead.last_contact_at || '';
                        break;
                      case 'visitCount':
                        value = lead.visit_count || 0;
                        break;
                      case 'project':
                        value = lead.project || '';
                        break;
                      case 'channelPartner':
                        value = lead.channel_partner?.name || '';
                        break;
                      case 'createdAt':
                        value = lead.created_at || '';
                        break;
                      case 'tags':
                        value = Array.isArray(lead.preferences) ? lead.preferences.join(', ') : '';
                        break;
                      case 'probability':
                        value = lead.probability || '';
                        break;
                      default:
                        value = '';
                    }
                    cells.push(`<td>${value}</td>`);
                  });
                  return `<tr>${cells.join('')}</tr>`;
                }).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => printWindow.close(), 100);
      };
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle funnel metric click
  const handleFunnelMetricClick = (metric) => {
    if (selectedFunnelMetric === metric.id) {
      // If clicking the same metric, deselect it
      setSelectedFunnelMetric(null);
      setFunnelMetricFilter(null);
      setFunnelMetricStatusFilter(null);
      // Don't reset stage filter - user might have manually set it
    } else {
      // Select the new metric and apply appropriate filter
      setSelectedFunnelMetric(metric.id);
      if (metric.specialFilter) {
        // Use special filter (contacted_filter, details_shared_filter, etc.)
        setFunnelMetricFilter(metric.specialFilter);
        setFunnelMetricStatusFilter(null);
        // Note: Stage filter is intentionally NOT reset to allow combining filters
      } else if (metric.stageFilter) {
        // Use stage filter
        setSelectedStage(metric.stageFilter);
        setFunnelMetricFilter(null);
        // If metric has statusFilter (like bookings_closed), set it
        if (metric.statusFilter) {
          setFunnelMetricStatusFilter(metric.statusFilter);
        } else {
          setFunnelMetricStatusFilter(null);
        }
      } else {
        // Total Leads - no filter, but don't reset existing filters
        setFunnelMetricFilter(null);
        setFunnelMetricStatusFilter(null);
      }
    }
    setPage(0); // Reset to first page
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as sort field with ascending order
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(0); // Reset to first page when sorting changes
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Leads Table
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
        "Let me deep dive or export what matters."
      </Typography>
      
      <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            size="small"
            onClick={handleColumnMenuOpen}
          >
            Columns
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              size="small"
              onClick={handleExportCSV}
              disabled={exporting || loading}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              size="small"
              onClick={handleExportExcel}
              disabled={exporting || loading}
            >
              Export Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              size="small"
              onClick={handleExportPDF}
              disabled={exporting || loading}
            >
              Export PDF
            </Button>
          </Stack>
        </Box>

        {/* Column Selection Menu */}
        <Menu
          anchorEl={columnMenuAnchor}
          open={columnMenuOpen}
          onClose={handleColumnMenuClose}
          PaperProps={{
            sx: {
              maxHeight: 400,
              width: '250px',
            }
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
              Select Columns
            </Typography>
            <Divider />
            {allColumns.map((column) => {
              const isAlwaysVisible = column.alwaysVisible || false;
              const isChecked = visibleColumns.includes(column.id);
              return (
                <MenuItem
                  key={column.id}
                  onClick={() => !isAlwaysVisible && handleToggleColumn(column.id)}
                  dense
                  disabled={isAlwaysVisible}
                  sx={{
                    opacity: isAlwaysVisible ? 0.6 : 1,
                    cursor: isAlwaysVisible ? 'default' : 'pointer'
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    size="small"
                    sx={{ py: 0 }}
                    disabled={isAlwaysVisible}
                  />
                  <ListItemText 
                    primary={column.label}
                    secondary={isAlwaysVisible ? 'Always visible' : undefined}
                    secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.65rem' } }}
                  />
                </MenuItem>
              );
            })}
          </Box>
        </Menu>

        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {selectedFunnelMetric && (
            <Chip
              label={`Filtered by: ${funnelMetrics.find(m => m.id === selectedFunnelMetric)?.label || 'Funnel Metric'}`}
              onDelete={() => handleFunnelMetricClick(funnelMetrics.find(m => m.id === selectedFunnelMetric))}
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
          {(searchTerm || selectedStage !== 'all' || selectedSource !== 'all' || selectedAgent !== 'all' || 
            selectedChannelPartner !== 'all' || selectedInterestedProperties.length > 0 ||
            budgetRange[0] > 0 || budgetRange[1] < 100000000 || visitCountFilter !== 'any' || 
            selectedTags !== 'all' || selectedProjectType !== 'all' || lastContactedFilter !== 'all' || selectedFunnelMetric) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setSelectedStage('all');
                setSelectedSource('all');
                setSelectedAgent('all');
                setSelectedChannelPartner('all');
                setSelectedInterestedProperties([]);
                setBudgetRange([0, 100000000]);
                setVisitCountFilter('any');
                setSelectedTags('all');
                setSelectedProjectType('all');
                setLastContactedFilter('all');
                setSelectedFunnelMetric(null);
                setFunnelMetricFilter(null);
                setFunnelMetricStatusFilter(null);
                setPage(0);
              }}
              sx={{ minWidth: 120 }}
            >
              Clear All Filters
            </Button>
          )}
          <TextField
            size="small"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <Autocomplete
            multiple
            size="small"
            options={filterOptions.intereseted_properties || []}
            value={Array.isArray(selectedInterestedProperties) ? selectedInterestedProperties : []}
            onChange={(event, newValue) => setSelectedInterestedProperties(Array.isArray(newValue) ? newValue : [])}
            getOptionLabel={(option) => typeof option === 'object' ? option.label : option}
            isOptionEqualToValue={(option, value) => 
              (typeof option === 'object' ? option.value : option) === (typeof value === 'object' ? value.value : value)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Interested Properties"
                placeholder="Select properties"
                disabled={loadingFilters}
              />
            )}
            renderTags={(value, getTagProps) =>
              (Array.isArray(value) ? value : []).map((option, index) => {
                const label = typeof option === 'object' ? option.label : option;
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={typeof option === 'object' ? option.value : option}
                    label={label}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.12)'
                      }
                    }}
                  />
                );
              })
            }
            sx={{ minWidth: 200 }}
            disabled={loadingFilters}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Agent</InputLabel>
            <Select
              value={selectedAgent}
              label="Agent"
              onChange={(e) => setSelectedAgent(e.target.value)}
              disabled={loadingFilters}
            >
              <MenuItem value="all">All Agents</MenuItem>
              {filterOptions.agents.map((agent) => (
                <MenuItem key={agent.value} value={agent.value}>
                  {agent.label}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Stage</InputLabel>
            <Select
              value={selectedStage}
              label="Stage"
              onChange={(e) => setSelectedStage(e.target.value)}
              disabled={loadingFilters}
            >
              <MenuItem value="all">All Stages</MenuItem>
              {filterOptions.lead_stage.map((stage) => (
                <MenuItem key={stage.value} value={stage.value}>
                  {stage.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={selectedSource}
              label="Source"
              onChange={(e) => setSelectedSource(e.target.value)}
              disabled={loadingFilters}
            >
              <MenuItem value="all">All Sources</MenuItem>
              {filterOptions.source.map((src) => (
                <MenuItem key={src.value} value={src.value}>
                  {src.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Channel Partner</InputLabel>
            <Select
              value={selectedChannelPartner}
              label="Channel Partner"
              onChange={(e) => setSelectedChannelPartner(e.target.value)}
              disabled={loadingFilters}
            >
              <MenuItem value="all">All Channel Partners</MenuItem>
              {filterOptions.channel_partner.map((partner) => (
                <MenuItem key={partner.value} value={partner.value}>
                  {partner.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Last Contacted</InputLabel>
            <Select
              value={lastContactedFilter}
              label="Last Contacted"
              onChange={handleLastContactedFilterChange}
              renderValue={(value) => {
                const labels = {
                  'all': 'All',
                  'today': 'Today',
                  'yesterday': 'Yesterday',
                  'this_month': 'This Month',
                  'this_quarter': 'This Quarter',
                  'custom': 'Custom'
                };
                const dateDisplay = getLastContactedDateDisplay();
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.25 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                      {labels[value] || value}
                    </Typography>
                    {dateDisplay && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.7rem', 
                          color: 'text.secondary',
                          lineHeight: 1,
                          mt: 0.25
                        }}
                      >
                        {dateDisplay}
                      </Typography>
                    )}
                  </Box>
                );
              }}
              sx={{
                fontSize: '0.875rem',
                height: 'auto',
                minHeight: getLastContactedDateDisplay() ? '48px' : '40px',
                '& .MuiSelect-select': {
                  py: 0.75,
                  px: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              <MenuItem value="all">
                <Box>
                  <Typography variant="body2">All</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="today">
                <Box>
                  <Typography variant="body2">Today</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {dayjs().format('MMM DD, YYYY')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="yesterday">
                <Box>
                  <Typography variant="body2">Yesterday</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {dayjs().subtract(1, 'day').format('MMM DD, YYYY')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="this_month">
                <Box>
                  <Typography variant="body2">This Month</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {`${dayjs().startOf('month').format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="this_quarter">
                <Box>
                  <Typography variant="body2">This Quarter</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {(() => {
                      const currentQuarter = Math.floor(dayjs().month() / 3);
                      const quarterStartMonth = currentQuarter * 3;
                      const quarterStart = dayjs().month(quarterStartMonth).startOf('month');
                      return `${quarterStart.format('MMM DD')} - ${dayjs().format('MMM DD, YYYY')}`;
                    })()}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="custom">
                <Box>
                  <Typography variant="body2">Custom</Typography>
                  {getLastContactedDateDisplay() && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {getLastContactedDateDisplay()}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Project Type</InputLabel>
            <Select
              value={selectedProjectType}
              label="Project Type"
              onChange={(e) => setSelectedProjectType(e.target.value)}
              disabled={loadingFilters}
            >
              <MenuItem value="all">All Types</MenuItem>
              {filterOptions.bhk_type.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tags</InputLabel>
            <Select
              value={selectedTags}
              label="Tags"
              onChange={(e) => setSelectedTags(e.target.value)}
              disabled={loadingFilters}
            >
              <MenuItem value="all">All Tags</MenuItem>
              {filterOptions.tags.map((tag) => (
                <MenuItem key={tag.value} value={tag.value}>
                  {tag.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

              <TextField
                size="small"
            label="Budget Range"
            value={budgetRange[0] === 0 && budgetRange[1] === 100000000 
              ? 'Any' 
              : `${formatBudgetCurrency(budgetRange[0])} - ${formatBudgetCurrency(budgetRange[1])}`
            }
            onClick={handleBudgetClick}
                InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <KeyboardArrowDownIcon 
                    sx={{ 
                      color: 'text.secondary',
                      transition: 'transform 0.2s',
                      transform: budgetPopoverOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }} 
                  />
                </InputAdornment>
              ),
                }}
            sx={{
              minWidth: 200,
              cursor: 'pointer',
              '& .MuiInputBase-root': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              },
              '& .MuiInputBase-input': {
                cursor: 'pointer',
              },
            }}
          />
          <Popover
            open={budgetPopoverOpen}
            anchorEl={budgetPopoverAnchor}
            onClose={handleBudgetPopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                p: 3,
                minWidth: 400,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }
            }}
          >
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
              Budget Range
            </Typography>
            <Slider
              value={tempBudgetRange}
              onChange={handleTempBudgetRangeChange}
              valueLabelDisplay="on"
              valueLabelFormat={formatBudgetCurrency}
              min={0}
              max={100000000} // 10 Cr
              step={500000} // 5 Lac steps
              sx={{
                color: '#ec4899', // Pink/magenta color
                height: 6,
                '& .MuiSlider-track': {
                  backgroundColor: '#ec4899',
                  border: 'none',
                },
                '& .MuiSlider-thumb': {
                  width: 20,
                  height: 20,
                  backgroundColor: '#fff',
                  border: '2px solid #ec4899',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  },
                  '&.Mui-active': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  },
                },
                '& .MuiSlider-valueLabel': {
                  backgroundColor: '#ec4899',
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  '&:before': {
                    borderTopColor: '#ec4899',
                  },
                  '& *': {
                    background: 'transparent',
                    color: '#fff',
                  },
                },
                '& .MuiSlider-rail': {
                  backgroundColor: alpha(theme.palette.grey[300], 0.5),
                  opacity: 1,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Min
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatBudgetCurrency(tempBudgetRange[0])}
                </Typography>
          </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Max
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatBudgetCurrency(tempBudgetRange[1])}
                </Typography>
        </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setTempBudgetRange([0, 100000000]);
                  setBudgetRange([0, 100000000]);
                  setPage(0);
                  setBudgetPopoverAnchor(null);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  },
                }}
              >
                Reset
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleBudgetPopoverClose}
                  sx={{
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'grey.400',
                      backgroundColor: alpha(theme.palette.grey[500], 0.08),
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleBudgetApply}
                  sx={{
                    backgroundColor: '#ec4899',
                    '&:hover': {
                      backgroundColor: '#db2777',
                    },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Popover>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Visit Count</InputLabel>
            <Select
              value={visitCountFilter}
              label="Visit Count"
              onChange={(e) => setVisitCountFilter(e.target.value)}
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="1">1</MenuItem>
              <MenuItem value="2">2</MenuItem>
              <MenuItem value="3">3</MenuItem>
              <MenuItem value="more_than_3">More than 3</MenuItem>
            </Select>
          </FormControl>

          </Box>

        <Box sx={{ mb: 2 }}>
          {/* Funnel Metrics */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5, 
            flexWrap: 'nowrap',
            p: 1.5,
            bgcolor: alpha(theme.palette.grey[50], 0.8),
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.grey[400], 0.3),
              borderRadius: 2,
            }
          }}>
            {funnelMetrics.map((metric, index) => {
              const isSelected = selectedFunnelMetric === metric.id;
              return (
              <Box 
                  key={metric.id}
                  onClick={() => handleFunnelMetricClick(metric)}
                sx={{ 
                  flex: '1 1 0',
                  minWidth: '160px',
                  display: 'flex',
                  alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    p: 1,
                    borderRadius: 1,
                    transition: 'all 0.2s',
                    border: isSelected ? `2px solid ${metric.color}` : `2px solid transparent`,
                    bgcolor: isSelected ? alpha(metric.color, 0.05) : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(metric.color, 0.08),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 8px ${alpha(metric.color, 0.2)}`
                    }
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 0.75,
                  bgcolor: alpha(metric.color, 0.15),
                  color: metric.color,
                  flexShrink: 0
                }}>
                  {metric.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                      {metric.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        {metric.percentage}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        {metric.value.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={metric.percentage} 
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: alpha(metric.color, 0.15),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: metric.color,
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
              );
            })}
          </Box>

          {/* Showing X to Y of Z leads text */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {pagination && pagination.total_count > 0 ? (
                <>
                  Showing <strong>{((pagination.current_page - 1) * pagination.page_size) + 1}</strong> to <strong>{Math.min(pagination.current_page * pagination.page_size, pagination.total_count)}</strong> of <strong>{pagination.total_count}</strong> leads
                </>
              ) : loading ? (
                <>Loading...</>
              ) : (
                <>No leads found</>
              )}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {visibleColumns.includes('leadDetails') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('name')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Lead Details
                        {sortField === 'name' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('source') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('source')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Source
                        {sortField === 'source' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('agent') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('agent')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Agent
                        {sortField === 'agent' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('team') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('team')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Team
                        {sortField === 'team' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('stage') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('stage')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Stage
                        {sortField === 'stage' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('status') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('status')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Status
                        {sortField === 'status' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('value') && (
                    <TableCell 
                      align="right"
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('value')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                        Value
                        {sortField === 'value' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('priority') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('priority')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Priority
                        {sortField === 'priority' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('lastContacted') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('last_contact_at')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Last Contacted
                        {sortField === 'last_contact_at' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('visitCount') && (
                    <TableCell 
                      align="center"
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('visit_count')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                        Visits Count
                        {sortField === 'visit_count' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('project') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('project')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Project
                        {sortField === 'project' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('channelPartner') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('channel_partner')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Channel Partner
                        {sortField === 'channel_partner' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('createdAt') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('created_at')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Created At
                        {sortField === 'created_at' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('tags') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('tags')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Tags/Preferences
                        {sortField === 'tags' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('probability') && (
                    <TableCell 
                      sx={{ 
                        py: 1, 
                        px: 1.5, 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } 
                      }}
                      onClick={() => handleSort('probability')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Probability
                        {sortField === 'probability' && (
                          sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {leadTableData && leadTableData.length > 0 ? leadTableData.map((lead) => (
                <TableRow 
                  key={lead.id}
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                >
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Box
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    >
                    {lead.lead_id && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          #{lead.lead_id}
                        </Typography>
                      )}
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        {lead.customer?.name || 'N/A'}
                      </Typography>
                      {lead.customer?.phone && (
                        <Typography variant="body2" color="text.secondary">
                          {lead.customer.phone}
                        </Typography>
                      )}
                      {lead.customer?.email && (
                        <Typography variant="body2" color="text.secondary">
                          {lead.customer.email}
                        </Typography>
                      )}
                      {lead.created_at && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          Created: {dayjs(lead.created_at).format('MMM DD, YYYY h:mm A')}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {visibleColumns.includes('source') && (
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Chip label={lead.source_verbose || lead.source || 'N/A'} size="small" variant="outlined" />
                  </TableCell>
                  )}
                  {visibleColumns.includes('agent') && (
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Box>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 0.5,
                          ...(lead.agent && {
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            }
                          })
                        }}
                        onClick={lead.agent ? () => navigate(`/reports/agent/${lead.agent.id}`) : undefined}
                      >
                        {lead.agent ? (
                          <>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: getRandomAvatarColor(),
                                color: 'white',
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                mr: 1
                              }}
                            >
                              {lead.agent.initials || 'U'}
                            </Avatar>
                            <Typography variant="body2" color={lead.agent ? "primary" : "text.secondary"}>
                              {lead.agent.name || 'Unassigned'}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </Box>
                      {lead.team && (
                        <Typography variant="caption" color="text.secondary">
                          {lead.team.name || 'N/A'}
                        </Typography>
                      )}
                      {lead.channel_partner && (
                        <Chip
                          label={lead.channel_partner.name}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  )}
                  {visibleColumns.includes('team') && (
                    <TableCell sx={{ py: 1, px: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {lead.team?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('stage') && (
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Chip 
                      label={lead.stage_verbose || lead.stage || 'N/A'} 
                      size="small" 
                      color={
                        lead.stage === 'closure' || lead.stage_verbose === 'Closure'
                          ? 'success' 
                          : lead.stage === 'negotiation' || lead.stage_verbose === 'Negotiation'
                            ? 'warning'
                            : lead.stage === 'qualified' || lead.stage === 'contacted' || lead.stage_verbose === 'Qualified' || lead.stage_verbose === 'Contacted'
                              ? 'info'
                              : 'primary'
                      }
                    />
                  </TableCell>
                  )}
                  {visibleColumns.includes('status') && (
                    <TableCell sx={{ py: 1, px: 1.5 }}>
                      <Chip 
                        label={lead.status_verbose || lead.status || 'N/A'} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  {visibleColumns.includes('value') && (
                  <TableCell align="right" sx={{ py: 1, px: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {lead.value || 'N/A'}
                    </Typography>
                  </TableCell>
                  )}
                  {visibleColumns.includes('priority') && (
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Chip
                      label={lead.priority || 'Medium'}
                      size="small"
                      color={lead.priority === 'Hot' ? 'error' : lead.priority === 'High' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  )}
                  {visibleColumns.includes('lastContacted') && (
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {lead.last_contact_at || 'Never'}
                    </Typography>
                  </TableCell>
                  )}
                  {visibleColumns.includes('visitCount') && (
                    <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                      <Chip
                        label={lead.visit_count || 0}
                        size="small"
                        sx={{
                          backgroundColor: lead.visit_count > 0 ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.grey[300], 0.5),
                          color: lead.visit_count > 0 ? theme.palette.success.main : theme.palette.text.secondary,
                          fontWeight: 'bold',
                          minWidth: '40px'
                        }}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.includes('project') && (
                  <TableCell sx={{ py: 1, px: 1.5 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '10px' }}>
                        {lead.project || 'No project assigned'}
                      </Typography>
                      {lead.preferences && lead.preferences.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {lead.preferences.slice(0, 2).map((pref, idx) => (
                            <Chip
                              key={idx}
                              label={pref}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          ))}
                          {lead.preferences.length > 2 && (
                            <Chip
                              label={`+${lead.preferences.length - 2}`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  )}
                  {visibleColumns.includes('channelPartner') && (
                    <TableCell sx={{ py: 1, px: 1.5 }}>
                      {lead.channel_partner ? (
                        <Chip
                          label={lead.channel_partner.name}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('createdAt') && (
                    <TableCell sx={{ py: 1, px: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {lead.created_at || 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('tags') && (
                    <TableCell sx={{ py: 1, px: 1.5 }}>
                      {lead.preferences && lead.preferences.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {lead.preferences.slice(0, 3).map((pref, idx) => (
                            <Chip
                              key={idx}
                              label={pref}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          ))}
                          {lead.preferences.length > 3 && (
                            <Chip
                              label={`+${lead.preferences.length - 3}`}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No tags
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('probability') && (
                    <TableCell sx={{ py: 1, px: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {lead.probability || 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4, px: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        No leads found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {pagination && pagination.total_pages > 1 && (
          <TablePagination
            component="div"
            count={pagination.total_count}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => {
              return `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`;
            }}
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              '& .MuiTablePagination-toolbar': {
                paddingLeft: 2,
                paddingRight: 2
              }
            }}
          />
        )}
      </Paper>

      {/* Custom Last Contacted Date Range Dialog */}
      <Dialog 
        open={customLastContactedDialogOpen} 
        onClose={handleCustomLastContactedDialogClose} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Select Custom Date Range for Last Contacted</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <DatePicker
                label="Start Date"
                value={customLastContactedStart}
                onChange={(newValue) => setCustomLastContactedStart(newValue)}
                maxDate={customLastContactedEnd}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={customLastContactedEnd}
                onChange={(newValue) => setCustomLastContactedEnd(newValue)}
                minDate={customLastContactedStart}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomLastContactedDialogClose}>Cancel</Button>
          <Button 
            onClick={handleCustomLastContactedApply} 
            variant="contained"
            disabled={!customLastContactedStart || !customLastContactedEnd || customLastContactedStart.isAfter(customLastContactedEnd)}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadTable;
